import { useState, useEffect, useCallback, useMemo } from 'react';

import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import { CircleHelpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import Table from '@/components/atoms/Table/Table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { getPermissions } from '@/redux/permissions/selectors';
import { ZendeskDepartment, ZendeskIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import styles from './styles.module.scss';

interface Departments {
  isLoading: boolean;
  departments: ZendeskDepartment[];
  updateIntegration: (data: ZendeskIntegration) => void;
  integration: ZendeskIntegration;
  isUpdating: boolean;
}

const DepartmentFieldsets = ({
  isLoading,
  departments,
  updateIntegration,
  integration,
  isUpdating,
}: Departments) => {
  const { t } = useTranslation();
  const [zDepartments, setZDepartments] = useState([]);
  const [initialZDepartments, setInitialZDepartments] = useState([]);
  const [noDepsToggle, setNoDepsToggle] = useState(
    integration?.config?.process_without_department || false
  );
  const [initialNoDeps, setInitialNoDeps] = useState(
    integration?.config?.process_without_department || false
  );

  useEffect(() => {
    setInitialNoDeps(integration?.config?.process_without_department || false);
  }, [integration?.config?.process_without_department]);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  useEffect(() => {
    if (departments) {
      const d = departments
        .map((d) => ({ ...d, selected: d.selected || false }))
        .sort(
          (a, b) => Number(a.enabled) - Number(b.enabled) || Number(a.selected) - Number(b.selected)
        );
      setZDepartments(d);
      setInitialZDepartments(cloneDeep(d));
    }
  }, [departments]);

  const handleMoveoToggle = useCallback(
    (index: number, e) => {
      setZDepartments((prev) => {
        const newDepartments = [...prev];
        newDepartments[index].selected = e.target.checked;
        return newDepartments;
      });
    },
    [setZDepartments]
  );

  const getColumns = useMemo(
    () => [
      {
        Header: t('common.name'),
        accessor: 'name',
        disableSortBy: true,
        width: 224,
        Cell: ({ value, row }) => {
          return (
            <div className={styles.departmentName}>
              {value}
              {row.original.description && (
                <InfoTooltip content={row.original.description} side="right">
                  <CircleHelpIcon size={12} className="text-muted-foreground" />
                </InfoTooltip>
              )}
            </div>
          );
        },
      },
      {
        Header: 'Zendesk',
        accessor: 'enabled',
        Cell: ({ value }) => (
          <Badge type="secondary" flavour={value ? 'successful' : 'warning'}>
            {value ? t('common.enabled') : t('common.disabled')}
          </Badge>
        ),
        disableSortBy: true,
        width: 90,
      },
      {
        Header: t('zendesk.department_id'),
        accessor: 'ccapi_id',
        disableSortBy: true,
        Cell: ({ value }) => (
          <div className={styles.copyButton}>
            <CopyButton data={value} iconSize={14} className={styles.copy}>
              <span className={styles.copy}>{`${t('common.copy')} id`}</span>
            </CopyButton>
          </div>
        ),
        width: 122,
      },
      {
        Header: 'Moveo',
        accessor: 'selected',
        Cell: ({ row, value }) => (
          <Switch
            name="active"
            checked={value}
            onCheckedChange={(e) => handleMoveoToggle(row.index, e)}
            disabled={!row.original.enabled || !canWrite || isUpdating}
          />
        ),
        disableSortBy: true,
        sortInverted: true,
        width: 76,
      },
    ],
    [canWrite, handleMoveoToggle, t, isUpdating]
  );

  const renderDepartments = () => {
    if (isLoading) {
      return <Skeleton className="h-5 w-[200px]" />;
    }

    return departments?.length > 0 ? (
      <Table
        columns={getColumns}
        data={zDepartments}
        sortable
        filterable={zDepartments?.length > 5}
        noGutters
        flexLayout
      />
    ) : (
      <p>{t('integrations.zendesk.no_departments')}</p>
    );
  };

  const isDirty = !isEqual(initialZDepartments, zDepartments) || noDepsToggle !== initialNoDeps;

  return (
    <FormFieldset
      title={t('integrations.zendesk.departments_title')}
      subtitle={t('integrations.zendesk.departments_subtitle')}
      isLoading={isUpdating}
      primaryButton={{ children: t('common.save'), disabled: !isDirty }}
      onSubmit={() =>
        updateIntegration({
          ...integration,
          config: {
            ...integration.config,
            process_without_department: noDepsToggle,
            departments: zDepartments.map((x) => ({
              ...x,
              description: x.description || undefined,
            })),
          },
        })
      }
    >
      <div className={styles.departments}>
        {renderDepartments()}
        <div className={styles.secondaryHeader}>
          <span className="text-sm leading-5 font-medium text-foreground">
            {t('integrations.zendesk.without_department')}
          </span>
          <span className="text-sm leading-5 font-medium text-foreground">Moveo</span>
        </div>
        <div className={styles.secondaryTable}>
          <span className="text-sm leading-5 font-normal">
            {t('integrations.zendesk.process_tickets_without_department')}
          </span>
          <span>
            <Switch
              name="process_tickets_without_department"
              checked={noDepsToggle}
              onCheckedChange={() => setNoDepsToggle(!noDepsToggle)}
              disabled={!canWrite || isUpdating}
            />
          </span>
        </div>
      </div>
    </FormFieldset>
  );
};

export default DepartmentFieldsets;
